import { ResourceArray, WithId } from "@medplum/core";
import { BundleEntry, Observation } from "@medplum/fhirtypes";

import { addDays, parseISO } from "date-fns";

import { usDateFormat } from "@/utils/datetime";
import { generateFullUrl, joinReferenceString } from "@/utils/medplum";

import { USUAL_GESTATION_DAYS } from "../constants/gestational-age";
import { LMP_CODING, LMP_RELIABILITY_CODING } from "../constants/lmp";

// --- Calculate ---------------------------------------------------------------

/**
 * Derive an estimated LMP date from an EDD date string.
 * Subtracts the standard gestation period (280 days) from the normalized EDD.
 *
 * @param eddDateString - ISO date or datetime string for the estimated delivery date
 * @returns Estimated last menstrual period as a Date object
 */
export function deriveLastMenstrualPeriodFromEDD(eddDateString: string): Date {
  return addDays(parseISO(eddDateString), -USUAL_GESTATION_DAYS);
}

// --- Display -----------------------------------------------------------------

/**
 * Returns a US-formatted display string for an LMP date, or `"--"` if absent.
 *
 * @param lastMenstrualPeriod - ISO date string for the last menstrual period
 * @returns Formatted date string (e.g. `"07/08/2024"`) or `"--"`
 */
export function getLastMenstrualPeriodDisplay(lastMenstrualPeriod: string | undefined): string {
  return lastMenstrualPeriod ? usDateFormat(lastMenstrualPeriod) : "--";
}

// --- Build -------------------------------------------------------------------

/**
 * Builds a `BundleEntry` containing a final LMP `Observation` (LOINC 8665-2)
 * for use in a FHIR transaction bundle.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param valueDateTime - ISO date string representing the last menstrual period date
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields
 *   (e.g. `focus`, `hasMember`); core fields always take precedence
 * @returns `BundleEntry<Observation>` containing the final LMP `Observation`
 */
export function buildLMPBundleEntry(
  patientReferenceString: string,
  valueDateTime: string,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      status: "final",
      code: { coding: [LMP_CODING] },
      subject: { reference: patientReferenceString },
      valueDateTime,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}

/** Builds a `BundleEntry for an updated LMP observation when the last menstrual period is removed by the client */
export function buildUpdatedLMPBundleEntry(
  lmpObservations: ResourceArray<WithId<Observation>>,
): BundleEntry<Observation>[] {
  return lmpObservations.map((observation) => ({
    request: {
      method: "PUT",
      url: joinReferenceString("Observation", observation.id),
    },
    resource: {
      ...observation,
      status: "cancelled",
      code: { coding: [LMP_CODING] },
    },
  }));
}

/**
 * Builds a `BundleEntry` for an LMP reliability `Observation` (LOINC 53661-5)
 * for use in a FHIR transaction bundle. Records whether the LMP date is certain
 * or estimated as a `valueString`.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param valueString - Reliability value (e.g. `"certain"`, `"approximate"`)
 * @param options.request - Optional bundle entry request override; defaults to `POST Observation`
 * @param options.resource - Optional partial `Observation` fields merged in before core fields;
 *   core fields always take precedence
 * @returns `BundleEntry<Observation>` containing the LMP reliability `Observation`
 */
export function buildLMPReliabilityBundleEntry(
  patientReferenceString: string,
  valueString: string,
  { request, resource }: { request?: BundleEntry["request"]; resource?: Partial<Observation> } = {},
): BundleEntry<Observation> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      ...resource,
      resourceType: "Observation",
      status: "final",
      code: { coding: [LMP_RELIABILITY_CODING] },
      subject: { reference: patientReferenceString },
      valueString,
    },
    request: {
      method: "POST",
      url: "Observation",
      ...request,
    },
  };
}
